import type {ReactNode} from 'react'
import ContactUsButton from '../components/ContactUsButton'
import './AboutPage.css'

type ValueIconName = 'cube' | 'shield' | 'heart'

interface Stat {
  value: string
  label: string
}

interface Value {
  icon: ValueIconName
  title: string
  body: string
}

const STATS: Stat[] = [
  {value: '2026', label: 'Year we shipped it'},
  {value: '∞', label: 'Custom devices'},
  {value: '€0', label: 'Monthly subscription'},
]

const VALUES: Value[] = [
  {
    icon: 'cube',
    title: 'Real 3D, build your own',
    body: "Every device is a parametric 3D model — and if the one you need isn't in the library, you can build it. Most tools won't let you do that.",
  },
  {
    icon: 'shield',
    title: 'A tool, not a service',
    body: "You shouldn't pay a monthly fee for something you reach for twice a quarter. Pay once for what you render, keep what you make.",
  },
  {
    icon: 'heart',
    title: 'Built by people who use it',
    body: 'We still use Ecranify on real client launches every month. Every rough edge we hit, we fix — usually that same week.',
  },
]

const VALUE_ICONS: Record<ValueIconName, ReactNode> = {
  cube: (
    <>
      <path d="M21 7L12 2 3 7l9 5 9-5z" />
      <path d="M3 7v10l9 5 9-5V7" />
      <path d="M12 12v10" />
    </>
  ),
  shield: <path d="M12 22s8-4 8-12V5l-8-3-8 3v5c0 8 8 12 8 12z" />,
  heart: (
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.6z" />
  ),
}

const ValueIcon = ({name, size = 18}: {name: ValueIconName; size?: number}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {VALUE_ICONS[name]}
  </svg>
)

const AboutPage = () => (
  <div className="about-root">
    <div className="ab-grid" aria-hidden />

    <main className="about-page">
      <header className="ab-hero">
        <span className="ab-eyebrow">
          <span className="ab-eyebrow-dot" />
          About Ecranify
        </span>
        <h1 className="ab-title">
          We built it for ourselves
          <br />
          first — <em>then opened the door.</em>
        </h1>
        <p className="ab-lede">
          Ecranify started in 2026 as an in-house tool at our software studio. Clients kept needing polished launch
          imagery, and — like most small software houses — we don&apos;t keep a dedicated designer on staff to
          hand-craft those screenshots. So we built a tool that could. Once it stopped feeling like a side-project, we
          made it a product.
        </p>
      </header>

      <div className="ab-stats">
        {STATS.map((stat) => (
          <div key={stat.label} className="ab-stat">
            <span className="ab-stat-value">{stat.value}</span>
            <span className="ab-stat-label">{stat.label}</span>
          </div>
        ))}
      </div>

      <section className="ab-section">
        <div>
          <h2 className="ab-h2">
            An in-house need, before it was <em>a product.</em>
          </h2>
          <div className="ab-body">
            <p>
              We run a small software house. Every few months we ship an app for a client, and more often than not the
              same client also wants the imagery to go with it — App Store screens, a Behance post, a launch tweet. Like
              most studios our size, we don&apos;t keep an in-house designer for that kind of work — but we also
              didn&apos;t want to send back plain, unthought-out screenshots. It looks amateur.
            </p>
            <p>
              So between client projects we wrote ourselves a small internal tool — real device frames, real 3D, drag a
              screen in, hit export. Once we&apos;d used it on enough launches to stop second-guessing it, the question
              became obvious: would anyone else want this?
            </p>
          </div>
        </div>
        <figure className="ab-quote">
          <p className="ab-quote-text">
            If we needed it, other studios probably needed it too. So we cleaned it up and shipped it.
          </p>
          <div className="ab-quote-author">
            <span className="ab-quote-avatar" />
            Vladislav V. — founder
          </div>
        </figure>
      </section>

      <section className="ab-section stacked">
        <header className="ab-stacked-head">
          <h2 className="ab-h2">
            Not new. <em>But missing the parts we wanted.</em>
          </h2>
          <div className="ab-body">
            <p>
              Mockup tools aren&apos;t a revolutionary idea — plenty of them exist. But none of the ones we tried let
              you build your own device. Most charge a recurring subscription for something you&apos;d use a handful of
              times a quarter. Few do real 3D.
            </p>
            <p>
              So Ecranify is the version we wanted to use ourselves, at a price we&apos;d be willing to pay — built
              around three things we won&apos;t bend on.
            </p>
          </div>
        </header>
        <div className="ab-values">
          {VALUES.map((value) => (
            <div key={value.title} className="ab-value">
              <span className="ab-value-icon">
                <ValueIcon name={value.icon} />
              </span>
              <div className="ab-value-title">{value.title}</div>
              <div className="ab-value-body">{value.body}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="ab-cta">
        <div className="ab-cta-inner">
          <span className="ab-eyebrow">
            <span className="ab-eyebrow-dot" />
            Get in touch
          </span>
          <h2 className="ab-cta-title">
            Question, feature request,
            <br />
            or a <em>client launch</em> you need help with?
          </h2>
          <p className="ab-cta-body">
            We read every message — usually we reply the same day. If something&apos;s broken, the person writing back
            is the person who wrote the code.
          </p>
          <div className="ab-cta-actions">
            <ContactUsButton />
          </div>
        </div>
      </section>
    </main>
  </div>
)

export default AboutPage
